import { Pencil, Tag, Trash2 } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { cn, EmptyState, IconButton, rowsFor, scopeHint, Section, sectionSkin } from './internal';
import type { SectionFrame } from './internal';
import type { CurrencyTextComponent, ExtrasAdjustment, ExtrasProduct } from './types';

/* The adjustment is the row: a name, what kind it is, and often a sentence
   explaining it. The product beside it is one short name repeated down a group,
   and the amount is a figure — neither grows usefully, so the middle column gets
   the share. */
const GRID = 'grid grid-cols-[minmax(0,1fr)_minmax(0,2.2fr)_minmax(0,0.8fr)]';
const GRID_EDIT = 'grid grid-cols-[minmax(0,1fr)_minmax(0,2.2fr)_minmax(0,0.8fr)_52px]';

export interface AdjustmentsSectionProps {
	products: ExtrasProduct[];
	CurrencyText: CurrencyTextComponent;
	/** Authoring actions on a row — editing a fee or removing it. Adding one is
	 * the section's own control, not a row's: see `action`. */
	actions?: {
		/** Handed the whole row, since an editor needs every field of it.
		 * Omitted where a surface can remove but not amend. */
		onEdit?: (adjustment: ExtrasAdjustment) => void;
		onRemove: (adjustmentId: string, name: string) => void;
	};
	/** The add control, in the header beside the count — where Delivery and
	 * Combination plans keep theirs. Which product it is for is the caller's
	 * question to ask, since only it knows how. */
	action?: ReactNode;
	/** Which page this is on — see `SectionFrame`. Defaults to the v1 panel. */
	frame?: SectionFrame;
}

export function AdjustmentsSection({
	products,
	CurrencyText,
	actions,
	action,
	frame,
}: AdjustmentsSectionProps) {
	const editable = !!actions;
	/* Only the products carrying something. Every product used to get a row
	   because the empty one was where its Add button lived; with Add in the
	   header, an empty row says "no fees" about a product nobody asked about. */
	const rows = rowsFor(products, (p) => p.adjustments.length > 0, false);
	const withAdjustments = products.filter((p) => p.adjustments.length > 0).length;
	const gridClass = editable ? GRID_EDIT : GRID;
	/* Gutter, head type and row height for the frame this is being drawn in — see
	   `sectionSkin`. Read off the prop rather than the context the leaf marks use:
	   these rows are built here, above the provider. */
	const skin = sectionSkin(frame);

	return (
		<Section
			icon={Tag}
			title="Adjustments"
			subtitle="fees and discounts on this offer"
			/* Nothing on a v2 card. Every count that could go here restates
			   something already on screen — the rows are right below, and what
			   they add up to is in the summary rail. The folding panel still
			   needs one: see `scopeHint`. */
			hint={frame === 'card' ? undefined : scopeHint(withAdjustments, products.length)}
			defaultOpen={withAdjustments > 0}
			frame={frame}
			action={action}
		>
			{rows.length === 0 && <EmptyState>No fees or discounts on this offer.</EmptyState>}

			{rows.length > 0 && (
				<div className={cn(gridClass, 'items-center gap-3 pb-1.5', skin.pad, skin.head)}>
					<div>Product</div>
					<div>Adjustment</div>
					<div className="text-right">Amount</div>
					{editable && <div />}
				</div>
			)}

			{rows.map((product) => (
				<Fragment key={product.offerId}>
					{(product.adjustments.length === 0 ? [null] : product.adjustments).map(
						(adj, i) => (
							<div
								key={adj?.id ?? product.offerId}
								className={cn(
									gridClass,
									'items-center gap-3',
									skin.pad,
									skin.row,
									// A product's adjustments read as one block: the rule
									// separates products, not the lines within one.
									i === 0 ? 'border-t border-line-1' : 'border-t border-line-1/40'
								)}
							>
								<span
									className={cn(
										'min-w-0 truncate text-sm font-medium',
										product.excluded ? 'text-text-3' : 'text-text-1'
									)}
								>
									{/* Named once per product — repeating it down a group of
									    its own fees says nothing the first row didn't. */}
									{i === 0 ? product.name : ''}
									{i === 0 && product.excluded && (
										<span className="ml-1.5 text-xs font-normal text-text-4">
											Not included
										</span>
									)}
								</span>

								{!adj ? (
									<div className="col-span-2 text-sm text-text-4">
										No fees or discounts.
									</div>
								) : (
									<>
										<div className="min-w-0">
											<div className="flex items-center gap-2">
												{/* A badge, not a coloured word: at 2xs in
												    front of the name it read as the first
												    word OF the name — "Fee 1" — and whether
												    a line adds or subtracts is the one thing
												    this table has to make obvious. */}
												<span
													className={cn(
														'shrink-0 rounded-[var(--r-1)] px-1.5 py-px text-2xs font-medium',
														adj.isFee
															? 'bg-status-warn-soft text-status-warn'
															: 'bg-status-success-soft text-status-success'
													)}
												>
													{adj.isFee ? 'Fee' : 'Discount'}
												</span>
												<span className="min-w-0 truncate text-sm text-text-1">
													{adj.name}
												</span>
											</div>
											{adj.description && (
												<div className="mt-0.5 truncate text-xs text-text-4">
													{adj.description}
												</div>
											)}
										</div>
										<span
											className={cn(
												'shrink-0 text-right font-mono text-sm',
												adj.isFee
													? 'text-status-warn'
													: 'text-status-success'
											)}
										>
											{adj.isFee ? '+' : '−'}
											{adj.isPercentage ? (
												`${adj.value}%`
											) : (
												<CurrencyText amount={Math.abs(adj.value)} />
											)}
										</span>
									</>
								)}

								{actions && (
									<div className="flex items-center justify-end gap-1.5">
										{adj && actions.onEdit && (
											<IconButton
												title="Edit"
												onClick={() => actions.onEdit!(adj)}
											>
												<Pencil size={11} />
											</IconButton>
										)}
										{adj && (
											<IconButton
												title="Remove"
												danger
												onClick={() => actions.onRemove(adj.id, adj.name)}
											>
												<Trash2 size={11} />
											</IconButton>
										)}
									</div>
								)}
							</div>
						)
					)}
				</Fragment>
			))}
		</Section>
	);
}
